<script setup lang="ts">
import { CirclePlus } from "@element-plus/icons-vue";
import { useStorage } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  HomePageType,
  OrgWideHubMessage,
  QuestionCodesToDisplay,
  WorkflowDto,
  WorkflowsDto,
  WorkflowStatus,
} from "~/api";
import WorkflowInstanceCreator from "~/Compliance/components/WorkflowInstanceCreator.vue";
import WorkflowResponseViewer from "~/Compliance/components/WorkflowResponseViewer.vue";
import WorkflowTable from "~/Compliance/components/WorkflowTable.vue";
import { useOrgWideHub } from "~/composables/useOrgWideHub";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import { parseDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";

type listItemType = WorkflowDto;
const loading = ref(true);
const route = useRoute();
const workflowDefinitionGuid: string = `${(route.meta.workflowDefinitionGuid as string) ?? ""}`;

const items = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();
const archivedWorkflows = ref<boolean>(false);
const workflowDialogOpen = ref(false);
const workflowBeingCreated = ref<WorkflowDto>();
const workflowOpenCounter = ref(0);

const statusList = ref<WorkflowStatus[]>([]);
const questionCodesToDisplay = ref<QuestionCodesToDisplay[]>([]);
const workflowDefinitionUpdatedAt = ref<string>("");
const entitiesMentioned = ref<Record<string, string> | null>();
const singleFormResponseStatuses = ref<Record<string, string> | null>();

const homePageType = ref<HomePageType>(HomePageType.WORKFLOWS); // start with this to avoid flickering
const orgWideHub = useOrgWideHub();

type WorkflowListFilters = {
  [workflowDefGuid: string]: {
    statusChecklist: string[];
    savedAt: string;
  };
};

type SelectedWorkflowListFilters = {
  workflowDef: string;
  allStatusForWorkflowDef: string[];
};

type QuestionToDisplayStorage = {
  questions: QuestionCodesToDisplay[];
  savedAt: string;
  forWorkflowDefGuid: string;
};

const defaultWorkflowListFilters: WorkflowListFilters = {};

const defaultSelectedWorkflowListFilters: SelectedWorkflowListFilters = {
  workflowDef: "",
  allStatusForWorkflowDef: [],
};

const showNewBtn = computed(() => {
  return !myInfoStore.isGuest;
});

const cachedSelectedWorkflowFilters = useStorage(
  "selectedWorkflowListFilters",
  defaultSelectedWorkflowListFilters,
);

const cachedWorkflowFilters = useStorage(
  "workflowListFilters",
  defaultWorkflowListFilters,
);

const cachedCustomQuestionCodesToDisplay = useStorage(
  "questionColumnsForSelectedWorkflowDef",
  {
    questions: [],
    savedAt: new Date().toISOString(),
    forWorkflowDefGuid: "",
  } as QuestionToDisplayStorage,
);

const workflowDefinitionName = computed(() => {
  return orgInfoStore.MyOrgInfo?.workflowDefinitionDtos?.find(
    (wd) => wd.guid === workflowDefinitionGuid,
  )?.name;
});

const refreshList = async () => {
  await getListAndInfo();
  showList();
};

const getListAndInfo = async () => {
  loading.value = true;
  await apiClient.workflowInstance
    .getWorkflowDtos(
      myInfoStore.currentSysOrgGuid,
      workflowDefinitionGuid,
      archivedWorkflows.value,
    )
    .then((res: WorkflowsDto) => {
      items.value = res.workflowDtos ?? [];

      singleFormResponseStatuses.value = res.singleFormResponseStatuses;

      workflowDefinitionUpdatedAt.value = res.workflowDefinitionUpdatedAt;
      statusList.value = res.statusList ?? [];
      questionCodesToDisplay.value = res.questionCodesToDisplay ?? [];
      entitiesMentioned.value = res.entitiesMentioned;

      cachedCustomQuestionCodesToDisplay.value.questions =
        questionCodesToDisplay.value;
      cachedCustomQuestionCodesToDisplay.value.savedAt =
        new Date().toISOString();
      cachedCustomQuestionCodesToDisplay.value.forWorkflowDefGuid =
        workflowDefinitionGuid;
      loading.value = false;
    });
};

const shouldShowItem = computed(() => {
  return showFullScreen.value && items.value.length === 1;
});

const showList = () => {
  // Show the list of workflows
  if (shouldShowItem.value) {
    showItem(items.value[0]);
  } else {
    sortList();
    listLoaded.value = true;
  }
};

const fetchArchivedWorkflows = async () => {
  // does our list contain archived workflows?
  const includesArchived = items.value.find(
    (wf) => parseDate(wf.workflowArchivedAt) !== null,
  );

  // if not, we need to fetch them
  if (!includesArchived) {
    await getListAndInfo();
    showList();
  }

  // if yes, they will be in the list already
};

const setCachedWorkflowFilters = () => {
  // remove outdated filters???
  // delete workflowListFilters.value[workflowDefinitionGuid];

  const workflowDefUpdated = workflowDefinitionUpdatedAt.value;

  const cachedFilters = cachedWorkflowFilters.value[workflowDefinitionGuid];

  const storageSavedAt = cachedFilters?.savedAt;

  if (!cachedFilters) {
    cachedWorkflowFilters.value[workflowDefinitionGuid] = {
      statusChecklist: [], // Start with empty filter list instead of all statuses
      savedAt: new Date().toISOString(),
    };
  }
  // if the workflow definition has been updated since the last time we saved the filters, then filter out any
  // status codes that are no longer valid
  else if (workflowDefUpdated && storageSavedAt < workflowDefUpdated) {
    const validCodes =
      statusList.value?.map((status) => status.code).filter(Boolean) || [];

    cachedWorkflowFilters.value[workflowDefinitionGuid].statusChecklist =
      cachedWorkflowFilters.value[
        workflowDefinitionGuid
      ].statusChecklist?.filter(
        (code) => code !== null && validCodes.includes(code),
      ) || [];

    cachedWorkflowFilters.value[workflowDefinitionGuid].savedAt =
      new Date().toISOString();
  }
};

const sortList = () => {
  items.value.sort((a: listItemType, b: listItemType) => {
    // reverse by date
    return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
  });
};

const setStatusListFilter = () => {
  cachedSelectedWorkflowFilters.value.allStatusForWorkflowDef =
    statusList.value?.map((s) => s.label).filter((label) => label !== null) ??
    [];
};

const getWorkflowStatusLabel = (statusCode: string) => {
  return statusList.value?.find((status) => status.code === statusCode)?.label;
};

const createWorkflow = () => {
  workflowBeingCreated.value = undefined;
  workflowOpenCounter.value++;
  workflowDialogOpen.value = true;
};

const handleAddWorkflow = (wf: WorkflowDto) => {
  items.value.push(wf);
  showItem(wf);
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

const itemsFiltered = computed(() => {
  if (items.value.length === 0) {
    return [];
  }

  if (!shouldShowStatusFilter.value) {
    return items.value;
  }

  const selectedWorkflowDefGuid =
    cachedSelectedWorkflowFilters.value?.workflowDef;

  const workflowDefFiltered = !!selectedWorkflowDefGuid;

  const statusFiltered =
    selectedWorkflowDefGuid &&
    cachedWorkflowFilters.value[selectedWorkflowDefGuid]?.statusChecklist
      ?.length !== 0;

  return items.value.filter((item) => {
    if (workflowDefFiltered) {
      if (selectedWorkflowDefGuid !== item.workflowDefinitionGuid) {
        return false;
      }
      if (statusFiltered && item.workflowStatus) {
        if (
          !cachedWorkflowFilters.value[
            selectedWorkflowDefGuid
          ]?.statusChecklist.includes(
            getWorkflowStatusLabel(item.workflowStatus) ?? "",
          )
        ) {
          return false;
        }
      }
    }

    if (!archivedWorkflows.value) {
      return item.workflowArchivedAt === null;
    }
    return true;
  });
});

const workflowUpdated = (workflow: WorkflowDto) => {
  const replaceIndex = items.value.findIndex(
    (item) => item.guid === workflow.guid,
  );
  if (replaceIndex !== -1) {
    items.value[replaceIndex] = workflow;
  }
};
const orgInfoStore = useOrgInfoStore();

const shouldShowList = computed(() => {
  return (
    homePageType.value === HomePageType.DEFAULT ||
    (homePageType.value === HomePageType.WORKFLOWS && items.value.length >= 2)
  );
});

const showFullScreen = computed(() => {
  return (
    homePageType.value === HomePageType.WORKFLOWS && items.value.length <= 1
  );
});

const shouldShowStatusFilter = computed(() => {
  const workflowDef = cachedSelectedWorkflowFilters.value?.workflowDef;
  if (!workflowDef || !cachedWorkflowFilters.value[workflowDef]) {
    return false;
  }

  // For WORKFLOWS homepage type, only show if more than 10 workflows
  if (homePageType.value === HomePageType.WORKFLOWS) {
    return (
      items.value.length > 10 &&
      cachedSelectedWorkflowFilters.value.allStatusForWorkflowDef.length > 0
    );
  }
  return cachedSelectedWorkflowFilters.value.allStatusForWorkflowDef.length > 0;
});

const shouldShowArchivedFilter = computed(() => {
  // Never show archived filter when homepage type is WORKFLOWS
  if (homePageType.value === HomePageType.WORKFLOWS) {
    return false;
  }
  // For DEFAULT homepage type, always show
  return true;
});

const listLoaded = ref(false);

const workflowListNeedsRefresh = (msg: OrgWideHubMessage) => {
  if (
    listLoaded.value &&
    msg.workflowDefinitionGuid_RefreshList &&
    workflowDefinitionGuid === msg.workflowDefinitionGuid_RefreshList
  ) {
    refreshList();
  }
};

onMounted(async () => {
  if (!workflowDefinitionGuid) {
    showError("No workflow definition GUID provided in route meta.");
    return;
  }

  await Promise.all([orgInfoStore.load(), myInfoStore.load()]);

  cachedSelectedWorkflowFilters.value.workflowDef = workflowDefinitionGuid;

  homePageType.value =
    myInfoStore.currentSysOrg.entityDto?.homePage ?? HomePageType.DEFAULT;

  await getListAndInfo();

  setCachedWorkflowFilters();

  setStatusListFilter();

  showList();

  await orgWideHub.ensure();

  await orgWideHub.on<OrgWideHubMessage>(
    "WorkflowsUpdated",
    workflowListNeedsRefresh,
  );
});
onUnmounted(() => {
  orgWideHub.off("WorkflowsUpdated", workflowListNeedsRefresh);
});
</script>

<template>
  <main class="WorkflowList" aria-label="Workflow List">
    <div v-if="shouldShowList">
      <section
        v-if="shouldShowStatusFilter"
        class="flexRow"
        aria-label="Workflow filters and actions"
      >
        <div>
          <output class="showingInTable" aria-live="polite">
            Showing {{ itemsFiltered.length }} of {{ items.length }}
          </output>
        </div>
        <div class="status-filters">
          <el-select
            v-model="
              cachedWorkflowFilters[cachedSelectedWorkflowFilters.workflowDef]
                .statusChecklist
            "
            placeholder="Filter by status"
            :clearable="true"
            :multiple="true"
            filterable
            aria-label="Filter workflows by status"
          >
            <el-option
              v-for="option in cachedSelectedWorkflowFilters.allStatusForWorkflowDef"
              :key="option"
              :label="option"
              :value="option"
            />
          </el-select>
        </div>
        <el-button
          v-if="showNewBtn"
          class="addButton"
          :icon="CirclePlus"
          title="Create a new workflow"
          aria-label="Create a new workflow"
          @click="createWorkflow"
        >
          {{ workflowDefinitionName }}
        </el-button>
        <div class="stackedFilters">
          <div class="flexRow">
            <el-checkbox
              v-if="shouldShowArchivedFilter"
              v-model="archivedWorkflows"
              class="archived-filter"
              aria-label="Include archived workflows in the list"
              @change="fetchArchivedWorkflows"
              >Include Archived</el-checkbox
            >
            <RefreshButton
              class="refresh"
              aria-label="Refresh workflow list"
              @click="refreshList"
            />
          </div>
        </div>
      </section>

      <WorkflowTable
        :data="itemsFiltered"
        :columns="questionCodesToDisplay"
        :status-list="statusList"
        :entities-mentioned="entitiesMentioned ?? null"
        :single-form-response-statuses="singleFormResponseStatuses ?? null"
        :loading="loading"
        :show-archived-column="archivedWorkflows"
        @workflow-click="showItem"
      />
    </div>
    <template v-else-if="!loading && shouldShowItem"></template>
    <div v-else-if="!loading && itemsFiltered.length === 0">
      <el-empty
        :description="'No ' + workflowDefinitionName + ' Workflows Found'"
      />
    </div>
    <div v-else>
      <el-empty description="Loading..." />
    </div>
  </main>

  <WorkflowInstanceCreator
    v-if="workflowDialogOpen"
    :key="workflowOpenCounter"
    v-model="workflowDialogOpen"
    :user-guid="myInfoStore.userInfo.userGuid"
    :organization-guid="myInfoStore.currentSysOrgGuid"
    :workflow-definition-guid="workflowDefinitionGuid"
    @add:workflow="handleAddWorkflow"
  />

  <WorkflowResponseViewer
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :full-screen="showFullScreen"
    :compliance-workflow="selectedRow"
    @close-drawer="closeDrawer"
    @update-workflow="workflowUpdated"
    @refresh-list="refreshList"
  />
</template>

<style lang="less">
.WorkflowList {
  .flexRow {
    margin: 0.5em 1em 0 1em;
    align-items: center;

    .showingInTable {
      align-self: flex-end;
      font-size: 14px;
      font-weight: 600;
      margin: 0.5em 1em 0 0;
      white-space: nowrap;
    }
    .stackedFilters {
      display: flex;
      flex-direction: column;
    }

    // Mobile responsiveness
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75em;
      margin: 0.5em 0.5em 0 0.5em;

      .showingInTable {
        align-self: stretch;
        margin: 0;
        text-align: center;
      }

      .status-filters {
        width: 100%;

        .el-select {
          width: 100%;
        }
      }

      .addButton {
        width: 100%;
        justify-content: center;
      }

      .stackedFilters {
        width: 100%;
        align-items: stretch;

        .flexRow {
          justify-content: space-between;
          align-items: center;
          margin: 0;

          .archived-filter {
            flex: 1;
          }

          .refresh {
            flex-shrink: 0;
          }
        }
      }
    }

    // Tablet responsiveness
    @media (max-width: 1024px) and (min-width: 769px) {
      flex-wrap: wrap;
      gap: 0.75em;

      .showingInTable {
        margin: 0.5em 0.5em 0 0;
      }

      .status-filters {
        min-width: 200px;
      }
    }
  }

  // Table responsiveness
  .el-table {
    @media (max-width: 768px) {
      font-size: 14px;

      .el-table__cell {
        padding: 8px 4px;
      }

      // Make table horizontally scrollable on mobile
      overflow-x: auto;
      min-width: 100%;
      display: block;
      white-space: nowrap;

      .el-table__header-wrapper,
      .el-table__body-wrapper {
        overflow-x: auto;
      }

      // Ensure minimum column widths for readability
      .el-table-column {
        min-width: 120px;
      }
    }

    @media (max-width: 480px) {
      font-size: 12px;

      .el-table__cell {
        padding: 6px 2px;
      }

      .el-table-column {
        min-width: 100px;
      }
    }
  }

  // Empty state responsiveness
  .el-empty {
    @media (max-width: 768px) {
      padding: 2em 1em;

      .el-empty__description {
        font-size: 14px;
      }
    }
  }
}

.el-drawer__body {
  overflow-y: scroll;
}

#pdfFrame {
  display: none;
  position: fixed;
  z-index: 5000;
  top: 5%;
  left: 5%;
  height: 90%;
  width: 90%;
}
</style>
